import { prismicClient } from '@/data';
import { usePrismicStaleTime } from '@/data/prismicHooks';
import { useActiveProductConfig } from '@/hooks/use-active-product';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import { hasMadeFirstDepositSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { PromotionDocumentDataExcludedStatesItem } from '@/types/prismic.generated';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';

import { PromotionWithTheme } from '../types';

type Props = ['promohub', boolean, string | undefined, boolean, string | undefined];

const fetcher = async (context: QueryFunctionContext<Props>) => {
    const [key, hasSuccessfulDeposit, userState, hasZeroBonus, promohubUID] = context.queryKey;
    const promoHub = await prismicClient.getByUID(key, promohubUID ?? '');
    const uids = promoHub.data.content
        .map(it => it.promotion as any)
        .map(it => it.id)
        .filter(Boolean);
    const promos = (await prismicClient.getAllByIDs<any>(uids, {
        graphQuery: `{
            promotion {
                ...promotionFields
                promo_theme {
                    ...promo_themeFields
                }
            }
        }`,
    })) as PromotionWithTheme[];

    let promotions = [...promos];
    // Exclude user from some promotions based on their state
    if (userState) {
        promotions = promos.filter(
            it =>
                !it.data?.excluded_states.some(
                    (state: { abbreviation: PromotionDocumentDataExcludedStatesItem['abbreviation'] }) =>
                        state.abbreviation === userState
                )
        );
    }

    // Filter out promotions exclusive to depositors if the user hasn't made a successful deposit
    if (!hasSuccessfulDeposit) {
        promotions = promotions.filter(it => !it.data?.exclude_non_depositor);
    }

    // Filter out promotions exclusive to depositors if the user has made a successful deposit and play though balance is 0
    if (hasSuccessfulDeposit && hasZeroBonus) {
        promotions = promotions.filter(it => !it.data?.exclude_depositor_zero_bonus);
    }

    return {
        title: promoHub.data.title,
        promotions,
        showRgFooter: promoHub.data.show_rg_footer ?? false,
    };
};

export const usePromohub = () => {
    const userState = useJurisdictionStore(state => state.jurisdiction);
    const productConfig = useActiveProductConfig();
    const staleTime = usePrismicStaleTime();
    const { data: hasSuccessfulDeposit } = useAuthUserConfig({
        select: hasMadeFirstDepositSelector,
    });
    const { bonus } = useActiveWallet({ refetchOnResume: false });
    const hasZeroBonus = bonus === 0;

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['promohub', !!hasSuccessfulDeposit, userState, hasZeroBonus, productConfig?.promohubUID],
        queryFn: fetcher,
        staleTime,
    });

    return {
        data,
        refetch,
        isLoading,
    };
};
